import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  HEADER_CONFIG,
  HEADER_CONST,
} from '../../common/constant/common-constant'
import {
  INVENTORY_STAGES,
  INVENTORY_TABS,
  PAGE_CONST,
  PERMISSION_FEATURE,
  PERMISSION_TYPE,
} from '../../common/constant/page-constant'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import usePagination from '../../common/hooks/usePagination'
import { redirect } from '../../common/utils/redirectionUtils'
import Loading from '../../components/Loding/Loading'
import Pagination from '../../components/PaginationComponent/Pagination'
import {
  getInventoryDetailsHelper,
  getInvoiceDetailsHelper,
  getProductDetailsHelper,
} from '../../helper/home-helper'
import Card from '../Common/Card/Card'
import MobileHeader from '../Common/Header/MobileHeader'
import ViewInventory from './ViewInventory'
import InventoryFilter from './components/InventoryFilter'

import { formatDate } from './../../common/utils/appUtils'
import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import { formatProductListLabel } from './../../common/utils/productUtils'
import './ManageInventory.scss'

// Helper function to calculate remaining days from expiry date
const calculateRemainingDays = (expiryDate) => {
  if (!expiryDate) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expiry = new Date(expiryDate)
  expiry.setHours(0, 0, 0, 0)
  const diffTime = expiry - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const ManageInventory = (props) => {
  const navigate = useNavigate()
  const isMobile = useCheckMobileScreen()

  const [viewDrawer, setViewDrawer] = useState(false)
  const [open, setOpen] = useState(false)
  const [inventoryData, setInventoryData] = useState([])
  const [productData, setProductData] = useState([])
  const [invoiceData, setInvoiceData] = useState(undefined)
  const [activeTab, setActiveTab] = useState(INVENTORY_TABS.PHARAMCEUTICAL)
  const [inventoryStage, setInventoryStage] = useState(
    INVENTORY_STAGES.PRODUCT_LISTING
  )
  const [activeProduct, setActiveProduct] = useState(undefined)

  const [headerOptions, setHeaderOptions] = useState({
    [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
    [HEADER_CONFIG.TITLE]: HEADER_CONST.MANAGE_INVENTORY,
    [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => redirect(navigate, PAGE_CONST.HOME),
  })

  const [inventoryLoading, setInventoryLoading] = useState(false)
  const [filterOptions, setFilterOptions] = useState({
    search: '',
  })
  const [showingAlternatives, setShowingAlternatives] = useState(false)
  const [zeroStockProducts, setZeroStockProducts] = useState([])
  const [reminderFilterDays, setReminderFilterDays] = useState(0)
  const [batchBreakdown, setBatchBreakdown] = useState({})

  // "Check All Branch" search mode — when on, search queries every branch of
  // the pharmacy (server-side) instead of just the active branch.
  const [checkAllBranch, setCheckAllBranch] = useState(false)

  // Check if user has cross-branch inventory permission
  const hasCrossBranchPermission = () => {
    // Owner/Admin users have full access
    if (props.loginData?.user_type === 'OWNER') {
      return true
    }

    const permissions = props.loginData?.permissions || []
    return permissions.some((p) => {
      const features = p.features || {}
      return (
        features[PERMISSION_FEATURE.CROSS_BRANCH_INVENTORY] === PERMISSION_TYPE.VIEW ||
        features[PERMISSION_FEATURE.CROSS_BRANCH_INVENTORY] === PERMISSION_TYPE.MANAGE
      )
    })
  }

  const handleCheckAllBranch = (checked) => {
    setCheckAllBranch(checked)
    pagination.goToPage(1)
  }

  const handleReminderFilter = (days) => {
    setReminderFilterDays(days)
    pagination.goToPage(1)
  }

  const updateFilterOptions = (newOptions) => {
    setFilterOptions((prev) => ({
      ...prev,
      ...newOptions,
    }))
    pagination.goToPage(1)
  }

  // Pagination hook
  const pagination = usePagination({
    initialPage: 1,
    initialLimit: 20,
  })
  useEffect(() => {
    const getInventoryDetails = async () => {
      setInventoryLoading(true)
      setShowingAlternatives(false)
      setZeroStockProducts([])
      
      try {
        const productType = activeTab === INVENTORY_TABS.PHARAMCEUTICAL
          ? 'PHARMACEUTICAL'
          : 'NON_PHARMACEUTICAL'

        const bodyData = {
          ...pagination.getQueryParams(),
          search: filterOptions.search || '',
          type: productType,
          all_branches: checkAllBranch,
        }

        const queryParams = { type: productType }

        if (reminderFilterDays > 0) {
          bodyData.expiry_days = reminderFilterDays
          queryParams.expiry_days = reminderFilterDays
        }

        const response = await getInventoryDetailsHelper(bodyData, queryParams)
        const { inventory, pagination: resPagination } = response

        if (!response || !inventory) {
          setInventoryData([])
          return
        }

        // Apply frontend filtering as backup if backend doesn't filter
        let filteredInventory = inventory

        // Filter by type (check both product_type and type fields)
        const expectedType =
          activeTab === INVENTORY_TABS.PHARAMCEUTICAL
            ? 'PHARMACEUTICAL'
            : 'NON_PHARMACEUTICAL'
        filteredInventory = filteredInventory.filter((item) => {
          const itemType = (item.product_type || item.type || '').toUpperCase()
          return itemType === expectedType
        })

        // Filter by search (product name and generic name)
        if (filterOptions.search && filterOptions.search.trim() !== '') {
          const searchTerm = filterOptions.search.toLowerCase().trim()
          
          // First, find products matching the search term
          const searchMatchedProducts = filteredInventory.filter(
            (item) =>
              item.name?.toLowerCase().includes(searchTerm) ||
              item.generic_name?.toLowerCase().includes(searchTerm)
          )
          
          // Check if all matched products have zero stock
          const zeroStockMatches = searchMatchedProducts.filter(
            (item) => !item.quantity || item.quantity === 0
          )
          const inStockMatches = searchMatchedProducts.filter(
            (item) => item.quantity && item.quantity > 0
          )
          
          // If there are zero stock products and no in-stock matches for the search term
          if (zeroStockMatches.length > 0 && inStockMatches.length === 0) {
            // Get unique generic names from zero stock products
            const genericNames = [
              ...new Set(
                zeroStockMatches
                  .map((item) => item.generic_name?.toLowerCase())
                  .filter(Boolean)
              ),
            ]
            
            // Find alternative products with same generic name that have stock
            const alternativeProducts = filteredInventory.filter(
              (item) =>
                item.quantity &&
                item.quantity > 0 &&
                item.generic_name &&
                genericNames.includes(item.generic_name.toLowerCase()) &&
                !zeroStockMatches.some((zp) => zp.id === item.id)
            )
            
            setZeroStockProducts(zeroStockMatches)

            if (alternativeProducts.length > 0) {
              setShowingAlternatives(true)
              const markedAlternatives = alternativeProducts.map((item) => ({
                ...item,
                isAlternative: true,
              }))
              filteredInventory = [...zeroStockMatches, ...markedAlternatives]
            } else {
              filteredInventory = zeroStockMatches
            }
          } else {
            // Normal search results (in-stock products found)
            filteredInventory = searchMatchedProducts
          }
        }

        setInventoryData(filteredInventory)

        if (resPagination?.count) pagination.setTotalItems(resPagination.count)
        if (resPagination?.total_pages)
          pagination.setTotalPages(resPagination.total_pages)
      } catch (error) {
        console.error('Error fetching inventory:', error)
        setInventoryData([])
      } finally {
        setInventoryLoading(false)
      }
    }

    getInventoryDetails()
  }, [pagination.page, pagination.limit, filterOptions, activeTab, reminderFilterDays, checkAllBranch])

  useEffect(() => {
    if (reminderFilterDays <= 0 || inventoryData.length === 0) {
      setBatchBreakdown({})
      return
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const reminderDate = new Date(today)
    reminderDate.setDate(reminderDate.getDate() + reminderFilterDays)

    const promises = inventoryData.map(product =>
      getProductDetailsHelper(product.id)
        .then(batches => {
          let expired = 0, nearExpired = 0, active = 0
          const total = batches?.length || 0

          batches?.forEach(batch => {
            if (!batch.expiry_date) { active++; return }
            const expiryDate = new Date(batch.expiry_date)
            expiryDate.setHours(0, 0, 0, 0)

            if (expiryDate < today) {
              expired++
            } else if (expiryDate <= reminderDate) {
              nearExpired++
            } else {
              active++
            }
          })

          return { id: product.id, expired, nearExpired, active, total }
        })
        .catch(() => ({ id: product.id, expired: 0, nearExpired: 0, active: 0, total: 0 }))
    )

    Promise.all(promises).then(results => {
      const map = {}
      results.forEach(r => { map[r.id] = r })
      setBatchBreakdown(map)
    })
  }, [inventoryData, reminderFilterDays])

  const handleProductListingClick = (product) => {
    setActiveProduct(product)
    getProductDetails(product.id)
  }

  const getProductDetails = (id) => {
    getProductDetailsHelper(id)
      .then((res) => {
        setProductData(res)
        setInventoryStage(INVENTORY_STAGES.PRODUCT_DETAILS)
      })
      .catch(console.log)
  }

  const getInvoiceDetails = (id) => {
    console.log('invoice details: ', id)
    getInvoiceDetailsHelper(id)
      .then((res) => {
        console.log('mc res: ', res)
        setInvoiceData(res)
        handleDrawerToggle()
      })
      .catch((err) => {
        console.log(err)
      })
  }
  const handleDrawerToggle = () => {
    setViewDrawer(!viewDrawer)
    setOpen(!open)
  }

  const handleHeaderConfigUpdate = (title, handleBackFunc) => {
    setHeaderOptions({
      [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
      [HEADER_CONFIG.TITLE]: title,
      [HEADER_CONFIG.HANDLE_BACK_FUNC]: handleBackFunc,
    })
  }

  useEffect(() => {
    if (!open && inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING) {
      handleHeaderConfigUpdate(HEADER_CONST.MANAGE_INVENTORY, () =>
        redirect(navigate, PAGE_CONST.HOME)
      )
    } else if (open) {
      handleHeaderConfigUpdate(HEADER_CONST.VIEW_INVENTORY, () => {
        handleDrawerToggle()
        setInventoryStage(INVENTORY_STAGES.PRODUCT_DETAILS)
      })
    } else if (inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS) {
      handleHeaderConfigUpdate(HEADER_CONST.VIEW_INVENTORY, () =>
        setInventoryStage(INVENTORY_STAGES.PRODUCT_LISTING)
      )
    }
  }, [open, inventoryStage])

  return (
    <>
      {isMobile && <MobileHeader headerOptions={headerOptions} />}

      <div className="pageBodyRightPanel">
        <InventoryFilter
          filterOptions={filterOptions}
          setFilterOptions={updateFilterOptions}
          inventoryStage={inventoryStage}
          setInventoryStage={setInventoryStage}
          activeTab={activeTab}
          inventoryData={inventoryData}
          onReminderFilter={handleReminderFilter}
          isReminderFilterActive={reminderFilterDays > 0}
          activeReminderDays={reminderFilterDays}
          checkAllBranch={checkAllBranch}
          onCheckAllBranch={handleCheckAllBranch}
          showCheckAllBranch={hasCrossBranchPermission()}
        />


        {/* Normal Inventory View */}
        {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
          <ul className="tabs dFlex mt-5">
            <li
              className={
                activeTab === INVENTORY_TABS.PHARAMCEUTICAL
                  ? 'tabItem active'
                  : 'tabItem'
              }
              onClick={() => setActiveTab(INVENTORY_TABS.PHARAMCEUTICAL)}
            >
              Pharmaceutical
            </li>
            <li
              className={
                activeTab === INVENTORY_TABS.NON_PHARMACEUTICAL
                  ? 'tabItem active'
                  : 'tabItem'
              }
              onClick={() => setActiveTab(INVENTORY_TABS.NON_PHARMACEUTICAL)}
            >
              Non Pharmaceutical
            </li>
          </ul>
        )}
        {inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS && (
          <div className="itemBased">
            <div className="itemName">
              {formatProductListLabel({
                name: activeProduct.name,
                size: activeProduct.size,
                dose: activeProduct.dose,
                strength: activeProduct.strength,
                category: activeProduct.category,
              })}
            </div>
            <div className="itemDetails">
              <span className="item name">Category: {activeProduct.category || 'N/A'}</span>
              <span className="item type">Balance Qty: {activeProduct.quantity || 0}</span>
              <span className="item type">Available Qty: {productData ? productData.filter(p => !p.is_expired && !(p.expiry_date && new Date(p.expiry_date) < new Date())).reduce((sum, p) => sum + (p.total_quantity || 0), 0) : (activeProduct.available_quantity ?? activeProduct.quantity ?? 0)}</span>
            </div>
          </div>
        )}
        {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
          <>
            {inventoryLoading && <Loading />}

            {!inventoryLoading && (
              <>
                {showingAlternatives && (
                  <div className="alternativeProductsNotice" style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#856404'
                  }}>
                    <strong>Note:</strong> The searched product is out of stock. Showing alternative products with the same generic name.
                  </div>
                )}

                {!showingAlternatives && zeroStockProducts.length > 0 && filterOptions.search && (
                  <div className="zeroStockNotice" style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#721c24'
                  }}>
                    <strong>No stock available for "{filterOptions.search}".</strong> No alternative products with the same generic name are in stock.
                  </div>
                )}
                
                <div className="productLlistWrap dFlex">
                  {inventoryData?.map((inventory) => {
                    const isOutOfStock = !inventory.quantity || inventory.quantity === 0
                    const isAlternative = inventory.isAlternative
                    const b = reminderFilterDays > 0 ? batchBreakdown[inventory.id] : null
                    
                    return (
                      <div
                        className="cardWrap"
                        key={`${inventory.branch_id}-${inventory.id}`}
                        style={{
                          position: 'relative',
                          opacity: isOutOfStock ? 0.7 : 1,
                        }}
                      >
                        {inventory.branch_name && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#0d6efd',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}>
                            {inventory.branch_name}
                          </div>
                        )}
                        {isOutOfStock && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}>
                            OUT OF STOCK
                          </div>
                        )}
                        {isAlternative && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}>
                            ALTERNATIVE
                          </div>
                        )}
                        <Card
                          headerProduct={{
                            productType: inventory.origin,
                            productCategory: inventory.category,
                            quantity: `Balance Qty: ${inventory.quantity || 0}`,
                            rpu: `Available Qty: ${inventory.available_quantity ?? inventory.quantity ?? 0}`,
                          }}
                          noHeaderBorder
                          strong={false}
                          productDesc={<>
                            {`Generic Name: ${inventory.generic_name || 'N/A'}`}
                            {b && (
                              <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                                {b.expired > 0 && <span style={{ color: '#fff', backgroundColor: '#dc3545', padding: '2px 8px', borderRadius: '4px' }}>Expired: {b.expired}/{b.total}</span>}
                                {b.nearExpired > 0 && <span style={{ color: '#fff', backgroundColor: '#f57c00', padding: '2px 8px', borderRadius: '4px' }}>Near Expired: {b.nearExpired}/{b.total}</span>}
                                {b.active > 0 && <span style={{ color: '#fff', backgroundColor: '#28a745', padding: '2px 8px', borderRadius: '4px' }}>Active: {b.active}/{b.total}</span>}
                              </span>
                            )}
                          </>}
                          headerMain={formatProductListLabel({
                          name: inventory.name,
                          size: inventory.size,
                          dose: inventory.dose,
                          strength: inventory.strength,
                          category: inventory.category,
                        })}
                          handleClick={() => {
                            // All-branch results span other branches; product
                            // details are current-branch scoped, so no drill-down.
                            if (!checkAllBranch) handleProductListingClick(inventory)
                          }}
                        />
                      </div>
                    )
                  })}
                </div>

                {inventoryData?.length === 0 && filterOptions.search && (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#666'
                  }}>
                    {zeroStockProducts.length > 0 ? (
                      <p>No stock available for "{filterOptions.search}"</p>
                    ) : (
                      <p>No products found for "{filterOptions.search}"</p>
                    )}
                  </div>
                )}

                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.goToPage}
                  onPrevPage={pagination.prevPage}
                  onNextPage={pagination.nextPage}
                  onFirstPage={pagination.firstPage}
                  onLastPage={pagination.lastPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  showFirstLast
                  maxPageButtons={5}
                />
              </>
            )}
          </>
        )}

        {inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS && (
          <div className="productLlistWrap dFlex">
            {productData?.map((product) => {
              const remainingDays = calculateRemainingDays(product.expiry_date)
              const isExpired = product.is_expired || (remainingDays !== null && remainingDays < 0)
              const isAvailableForSale = !isExpired && product.total_quantity > 0

              return (
                <div
                  className="cardWrap"
                  key={product.id}
                  style={{
                    position: 'relative',
                    opacity: isExpired ? 0.6 : 1,
                  }}
                >
                  {isExpired && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      zIndex: 1,
                    }}>
                      EXPIRED
                    </div>
                  )}
                  {!isExpired && product.total_quantity === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      zIndex: 1,
                    }}>
                      SOLD OUT
                    </div>
                  )}
                  {isAvailableForSale && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      zIndex: 1,
                    }}>
                      AVAILABLE
                    </div>
                  )}
                  <Card
                    headerSup={`Expiry: ${formatDate(product.expiry_date)} (${remainingDays !== null ? remainingDays + ' days left' : 'N/A'})`}
                    headerProduct={{
                      productType: `Invoice No.: ${product.invoice_number}`,
                      productCategory: `CPU: BDT ${currencyFormatter(parseFloat(product?.cpu).toFixed(2))}`,
                      quantity: `Qty: ${product.total_quantity}`,
                      rpu: `RPU: ${product.rate_per_unit ? currencyFormatter(product.rate_per_unit) : 'N/A'}`,
                    }}
                    noHeaderBorder
                    strong={false}
                    headerMain={`${product.pharma_company}`}
                    productDesc={product.received_quantity !== undefined && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', borderTop: '1px dashed #ddd', paddingTop: '8px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>Inventory Adjustment</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
                          <span>Received:</span><span style={{ textAlign: 'right', fontWeight: 500 }}>{product.received_quantity}</span>
                          {product.sold_quantity > 0 && <><span style={{ color: '#0d6efd' }}>Sold:</span><span style={{ textAlign: 'right', color: '#0d6efd' }}>-{product.sold_quantity}</span></>}
                          {product.damage_loss_quantity > 0 && <><span style={{ color: '#dc3545' }}>Damage/Loss:</span><span style={{ textAlign: 'right', color: '#dc3545' }}>-{product.damage_loss_quantity}</span></>}
                          {product.store_return_quantity > 0 && <><span style={{ color: '#f57c00' }}>Store Return:</span><span style={{ textAlign: 'right', color: '#f57c00' }}>-{product.store_return_quantity}</span></>}
                          {product.expired_quantity > 0 && <><span style={{ color: '#6c757d' }}>Expired:</span><span style={{ textAlign: 'right', color: '#6c757d' }}>-{product.expired_quantity}</span></>}
                          {product.hold_quantity > 0 && <><span style={{ color: '#e65100' }}>On Hold (Sales):</span><span style={{ textAlign: 'right', color: '#e65100' }}>{product.hold_quantity}</span></>}
                          <span style={{ fontWeight: 600, borderTop: '1px solid #ddd', paddingTop: '2px' }}>Available:</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, borderTop: '1px solid #ddd', paddingTop: '2px' }}>{product.total_quantity}</span>
                        </div>
                      </div>
                    )}
                    handleClick={() =>
                      product.product_receiving_id
                        ? getInvoiceDetails(product.product_receiving_id)
                        : null
                    }
                  />
                </div>
              )
            })}
          </div>
        )}

        <ViewInventory
          open={open}
          handleDrawerToggle={handleDrawerToggle}
          invoiceData={invoiceData}
        />
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    loginData: state.loginDataReducer?.loginData || {},
  }
}

export default connect(mapStateToProps)(ManageInventory)
